/**
 * demoAnalytics.ts — Source Attribution & Signup Handoff
 *
 * Captures ?src= param on first load for funnel attribution.
 * Stores last action before signup for post-registration context handoff.
 *
 * Place at: src/demo/demoAnalytics.ts
 */

const SOURCE_KEY = 'coretrak.demo.source';
const LAST_ACTION_KEY = 'coretrak.demo.lastAction';

/**
 * Captures ?src= param on first load (e.g. ?demo=1&src=reddit).
 * Called automatically by initDemoMode() at boot — attribution is guaranteed
 * regardless of which screen renders first.
 */
export function captureDemoSource(): void {
  try {
    if (sessionStorage.getItem(SOURCE_KEY)) return; // already captured
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(
      window.location.hash.split('?')[1] || '',
    );
    const src = urlParams.get('src') || hashParams.get('src');
    if (src) {
      sessionStorage.setItem(SOURCE_KEY, src);
    }
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

/**
 * Returns the captured source param, or null if none.
 */
export function getDemoSource(): string | null {
  try {
    return sessionStorage.getItem(SOURCE_KEY);
  } catch {
    return null;
  }
}

/**
 * Stores the last action the user tried before tapping signup.
 * After registration, route them to the relevant screen + show
 * a one-time "Now log your first X" message.
 */
export function saveDemoLastAction(action: string): void {
  try {
    sessionStorage.setItem(LAST_ACTION_KEY, action);
  } catch {
    /* ignore */
  }
}

/**
 * Returns the last demo action, or null if none stored.
 */
export function getDemoLastAction(): string | null {
  try {
    return sessionStorage.getItem(LAST_ACTION_KEY);
  } catch {
    return null;
  }
}

/**
 * Fires lightweight conversion events.
 * Placeholder — wire to your analytics provider (Mixpanel, PostHog, etc.)
 *
 * Events:
 *   demo_view       — user loaded demo mode
 *   demo_cta_click  — user tapped signup from banner
 *   demo_write_blocked — user tried a write action
 *   demo_signup_click  — user tapped signup from write modal
 *   demo_guided_step   — user completed a guided tour step
 */
export function trackDemoEvent(
  event: string,
  metadata?: Record<string, string>,
): void {
  const src = getDemoSource();
  const payload = { ...metadata, ...(src ? { src } : {}) };

  // Console log for now — replace with real analytics call
  console.log('[demoAnalytics]', event, payload);
}
